import { useEffect, useState } from "react";
import type { FileWithPath } from "react-dropzone";

import type {
  GenerateInvoiceUploadUrlMutationVariables,
  GenerateSupplierBillingAdjusmentDocumentUploadUrlMutationVariables,
  OrderReturnLabelUploadUrlQueryVariables,
} from "~/graphql/graphql-new.gen";
import {
  useGenerateInvoiceUploadUrlMutation,
  useGenerateProfilePictureUploadUrlMutation,
  useGenerateSupplierBillingAdjusmentDocumentUploadUrlMutation,
  useOrderReturnLabelUploadUrlQuery,
  useSubmitDownPaymentMutation,
  useSubmitInvoiceMutation,
  useUpdateProfilePictureMutation,
} from "~/graphql/graphql-new.gen";
import type {
  Exact,
  UploadUrlQuery,
  UploadUrlQueryVariables,
} from "~/graphql/graphql.gen";
import {
  UploadUrlDocument,
  useUploadCsvFileMutation,
} from "~/graphql/graphql.gen";
import { useFetchData } from "~/hooks/use-fetcher";

const uploadFile = (signedUrl: string, upload: FileWithPath) => {
  return fetch(signedUrl, {
    method: "PUT",
    headers: {
      "Content-Type": upload.type,
      "Cache-Control": "no-cache",
    },
    body: upload,
  }).then((response) => {
    if (!response.ok) throw new Error("Couldn't upload file");
    return response;
  });
};

export type UploadCsvMutation = {
  uploadedCsvUrl?: string | null | undefined;
};

export type UploadCsvMutationVariables = Exact<{
  saleId: string;
  file: FileWithPath;
}>;

/**
 * Uploads a CSV file
 */
export const useUploadCsvMutation = (): [
  (variables: UploadCsvMutationVariables) => Promise<void>,
  {
    data: UploadCsvMutation | undefined;
    loading: boolean;
    error: Error | undefined;
  },
] => {
  const [data, setData] = useState<UploadCsvMutation | undefined>();
  const [error, setError] = useState<Error | undefined>();
  const [loading, setLoading] = useState(false);

  const fetchUploadUrl = useFetchData<UploadUrlQuery, UploadUrlQueryVariables>(
    UploadUrlDocument,
  );

  const {
    mutateAsync: uploadCsvFileMutation,
    data: uploadCsvFileData,
    error: uploadCsvFileError,
  } = useUploadCsvFileMutation();

  useEffect(() => {
    if (uploadCsvFileError instanceof Error) {
      setError(uploadCsvFileError);
    }
  }, [uploadCsvFileError]);

  useEffect(() => {
    if (uploadCsvFileData?.uploadCsvFile) {
      setData({
        uploadedCsvUrl: uploadCsvFileData?.uploadCsvFile,
      });
    }
  }, [uploadCsvFileData]);

  const uploadCsvMutation = async (variables: UploadCsvMutationVariables) => {
    setLoading(true);

    const { getUploadUrl2 } = await fetchUploadUrl({
      fileName: variables.file.name,
      fileType: variables.file.type,
    });

    if (getUploadUrl2) {
      try {
        await uploadFile(getUploadUrl2.url, variables.file);
      } catch (error) {
        setError(error as Error);
      }

      await uploadCsvFileMutation({
        saleId: variables.saleId,
        url: getUploadUrl2.key,
      });
    }

    setLoading(false);
  };

  return [uploadCsvMutation, { data, loading, error }];
};

export type UploadPictureMutation = {
  uploadedPictureUrl?: string | null | undefined;
};

export type UploadPictureMutationVariables = Exact<{
  file: File;
}>;

/**
 * Uploads a picture file
 */
export const useUploadPictureMutation = (): [
  (variables: UploadPictureMutationVariables) => Promise<void>,
  {
    data: UploadPictureMutation | undefined;
    loading: boolean;
    error: Error | undefined;
  },
] => {
  const [data, setData] = useState<UploadPictureMutation | undefined>();
  const [error, setError] = useState<Error | undefined>();
  const [loading, setLoading] = useState(false);

  const {
    mutateAsync: uploadProfileUrlMutation,
    error: uploadProfileUrlError,
  } = useGenerateProfilePictureUploadUrlMutation();

  const {
    mutateAsync: updateProfilePictureMutation,
    error: updateProfilePictureError,
  } = useUpdateProfilePictureMutation();

  useEffect(() => {
    if (uploadProfileUrlError instanceof Error) {
      setError(uploadProfileUrlError);
    }
  }, [uploadProfileUrlError]);

  useEffect(() => {
    if (updateProfilePictureError instanceof Error) {
      setError(updateProfilePictureError);
    }
  }, [updateProfilePictureError]);

  const uploadPictureMutation = async (
    variables: UploadPictureMutationVariables,
  ) => {
    setLoading(true);

    const { generateProfilePictureUploadUrl } = await uploadProfileUrlMutation({
      upload: {
        fileName: variables.file.name,
        fileMimeType: variables.file.type,
        fileSize: variables.file.size,
      },
    });

    if (generateProfilePictureUploadUrl) {
      try {
        await uploadFile(generateProfilePictureUploadUrl.url, variables.file);

        setData({
          uploadedPictureUrl: generateProfilePictureUploadUrl.url,
        });
      } catch (error) {
        setError(error as Error);
      }

      await updateProfilePictureMutation({
        url: generateProfilePictureUploadUrl.url,
      });
    }

    setLoading(false);
  };

  return [uploadPictureMutation, { data, loading, error }];
};

/**
 * Uploads a down payment file
 */
export const useDownPaymentUploadMutation = (): [
  (variables: {
    file: File;
    saleId: GenerateInvoiceUploadUrlMutationVariables["saleId"];
  }) => Promise<void>,
  {
    data: UploadPictureMutation | undefined;
    error: Error | undefined;
    loading: boolean;
    progress: number;
  },
] => {
  const [data, setData] = useState<UploadPictureMutation | undefined>();
  const [error, setError] = useState<Error | undefined>();
  const [progress, setProgress] = useState(0);
  const [loading, setLoading] = useState(false);

  const {
    mutateAsync: generateInvoiceUploadUrlMutation,
    error: generateInvoiceUploadUrlError,
  } = useGenerateInvoiceUploadUrlMutation();

  const { mutateAsync: submitDownPaymentMutation, error: submitInvoiceError } =
    useSubmitDownPaymentMutation();

  useEffect(() => {
    if (generateInvoiceUploadUrlError instanceof Error) {
      setError(generateInvoiceUploadUrlError);
    }
  }, [generateInvoiceUploadUrlError]);

  useEffect(() => {
    if (submitInvoiceError instanceof Error) {
      setError(submitInvoiceError);
    }
  }, [submitInvoiceError]);

  const downPaymentUploadMutation = async (variables: {
    file: File;
    saleId: GenerateInvoiceUploadUrlMutationVariables["saleId"];
  }) => {
    setProgress(0);
    setLoading(true);

    try {
      const { generateInvoiceUploadUrl } =
        await generateInvoiceUploadUrlMutation({
          saleId: variables.saleId,
          upload: {
            fileName: variables.file.name,
            fileMimeType: variables.file.type,
            fileSize: variables.file.size,
          },
        });

      if (generateInvoiceUploadUrl) {
        try {
          const request = new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open("PUT", generateInvoiceUploadUrl.url);
            xhr.onload = () => {
              if (xhr.status >= 200 && xhr.status < 300) {
                resolve(xhr.response);
              } else {
                reject(new Error(xhr.statusText));
              }
            };
            xhr.upload.onprogress = (event) => {
              setProgress(Math.round(100 * (event.loaded / event.total)));
            };
            xhr.onerror = () => {
              reject(new Error(xhr.statusText));
            };
            xhr.send(variables.file);
          });
          await request;

          setData({
            uploadedPictureUrl: generateInvoiceUploadUrl.url,
          });
        } catch (error) {
          setError(error as Error);
          throw error;
        }

        await submitDownPaymentMutation({
          saleId: variables.saleId,
          input: {
            invoiceStorageFileKey: generateInvoiceUploadUrl.key,
          },
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return [downPaymentUploadMutation, { data, error, loading, progress }];
};

/**
 * Uploads an invoice file
 */
export const useInvoiceUploadMutation = (): [
  (variables: {
    file: File;
    saleId: GenerateInvoiceUploadUrlMutationVariables["saleId"];
  }) => Promise<void>,
  {
    data: UploadPictureMutation | undefined;
    error: Error | undefined;
    loading: boolean;
    progress: number;
  },
] => {
  const [data, setData] = useState<UploadPictureMutation | undefined>();
  const [error, setError] = useState<Error | undefined>();
  const [progress, setProgress] = useState(0);
  const [loading, setLoading] = useState(false);

  const {
    mutateAsync: generateInvoiceUploadUrlMutation,
    error: generateInvoiceUploadUrlError,
  } = useGenerateInvoiceUploadUrlMutation();

  const { mutateAsync: submitInvoiceMutation, error: submitInvoiceError } =
    useSubmitInvoiceMutation();

  useEffect(() => {
    if (generateInvoiceUploadUrlError instanceof Error) {
      setError(generateInvoiceUploadUrlError);
    }
  }, [generateInvoiceUploadUrlError]);

  useEffect(() => {
    if (submitInvoiceError instanceof Error) {
      setError(submitInvoiceError);
    }
  }, [submitInvoiceError]);

  const invoiceUploadMutation = async (variables: {
    file: File;
    saleId: GenerateInvoiceUploadUrlMutationVariables["saleId"];
  }) => {
    setProgress(0);
    setLoading(true);

    try {
      const { generateInvoiceUploadUrl } =
        await generateInvoiceUploadUrlMutation({
          saleId: variables.saleId,
          upload: {
            fileName: variables.file.name,
            fileMimeType: variables.file.type,
            fileSize: variables.file.size,
          },
        });

      if (generateInvoiceUploadUrl) {
        try {
          const request = new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open("PUT", generateInvoiceUploadUrl.url);
            xhr.onload = () => {
              if (xhr.status >= 200 && xhr.status < 300) {
                resolve(xhr.response);
              } else {
                reject(new Error(xhr.statusText));
              }
            };
            xhr.upload.onprogress = (event) => {
              setProgress(Math.round(100 * (event.loaded / event.total)));
            };
            xhr.onerror = () => {
              reject(new Error(xhr.statusText));
            };
            xhr.send(variables.file);
          });
          await request;

          setData({
            uploadedPictureUrl: generateInvoiceUploadUrl.url,
          });
        } catch (error) {
          setError(error as Error);
          throw error;
        }

        await submitInvoiceMutation({
          saleId: variables.saleId,
          input: {
            invoiceStorageFileKey: generateInvoiceUploadUrl.key,
          },
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return [invoiceUploadMutation, { data, error, loading, progress }];
};

/**
 * Uploads a return slip file
 */
export const useOrderReturnSlipUploadMutation = ({
  orderId,
}: {
  orderId: OrderReturnLabelUploadUrlQueryVariables["input"]["orderId"];
}): [
  (variables: { file: File }) => Promise<
    | {
        uploadedKey: string;
      }
    | undefined
  >,
  {
    data: { uploadedKey: string } | undefined;
    error: Error | undefined;
    loading: boolean;
    progress: number;
  },
] => {
  const [data, setData] = useState<
    | {
        uploadedKey: string;
      }
    | undefined
  >();
  const [error, setError] = useState<Error | undefined>();
  const [progress, setProgress] = useState(0);
  const [loading, setLoading] = useState(false);

  const {
    refetch: generateOrderReturnLabelUploadUrlMutation,
    error: generateOrderReturnLabelUploadUrlError,
  } = useOrderReturnLabelUploadUrlQuery(
    {
      input: {
        orderId: orderId,
      },
    },
    {
      enabled: false,
      select: (data) => data.orderReturnLabelUploadUrl,
    },
  );

  useEffect(() => {
    if (generateOrderReturnLabelUploadUrlError instanceof Error) {
      setError(generateOrderReturnLabelUploadUrlError);
    }
  }, [generateOrderReturnLabelUploadUrlError]);

  const returnSlipUploadMutation = async (variables: { file: File }) => {
    setProgress(0);
    setLoading(true);

    try {
      const { data: generateOrderReturnLabelUploadUrl } =
        await generateOrderReturnLabelUploadUrlMutation();

      if (generateOrderReturnLabelUploadUrl) {
        try {
          const request = new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open("PUT", generateOrderReturnLabelUploadUrl.url);
            xhr.onload = () => {
              if (xhr.status >= 200 && xhr.status < 300) {
                resolve(xhr.response);
              } else {
                reject(new Error(xhr.statusText));
              }
            };
            xhr.upload.onprogress = (event) => {
              setProgress(Math.round(100 * (event.loaded / event.total)));
            };
            xhr.onerror = () => {
              reject(new Error(xhr.statusText));
            };
            xhr.send(variables.file);
          });
          await request;

          setData({
            uploadedKey: generateOrderReturnLabelUploadUrl.key,
          });

          return {
            uploadedKey: generateOrderReturnLabelUploadUrl.key,
          };
        } catch (error) {
          setError(error as Error);
          throw error;
        }
      }
    } finally {
      setLoading(false);
    }
  };

  return [returnSlipUploadMutation, { data, error, loading, progress }];
};

/**
 * Uploads an financial adjustment file
 */
export const useFinancialAdjustmentAttachmentUploadMutation = (): [
  (variables: {
    file: File;
    saleId: GenerateSupplierBillingAdjusmentDocumentUploadUrlMutationVariables["saleId"];
  }) => Promise<
    | {
        uploadedUrl: string;
      }
    | undefined
  >,
  {
    data:
      | {
          uploadedKey: string;
        }
      | undefined;
    error: Error | undefined;
    loading: boolean;
    progress: number;
  },
] => {
  const [data, setData] = useState<
    | {
        uploadedKey: string;
      }
    | undefined
  >();
  const [error, setError] = useState<Error | undefined>();
  const [progress, setProgress] = useState(0);
  const [loading, setLoading] = useState(false);

  const {
    mutateAsync: generateBillingAdjustmentDocumentUploadUrlMutation,
    error: generateBillingAdjustmentDocumentUploadUrlError,
  } = useGenerateSupplierBillingAdjusmentDocumentUploadUrlMutation();

  useEffect(() => {
    if (generateBillingAdjustmentDocumentUploadUrlError instanceof Error) {
      setError(generateBillingAdjustmentDocumentUploadUrlError);
    }
  }, [generateBillingAdjustmentDocumentUploadUrlError]);

  const billingAdjustmentDocumentUploadMutation = async (variables: {
    file: File;
    saleId: GenerateSupplierBillingAdjusmentDocumentUploadUrlMutationVariables["saleId"];
  }) => {
    setProgress(0);
    setLoading(true);

    try {
      const { generateSupplierBillingAdjusmentDocumentUploadUrl } =
        await generateBillingAdjustmentDocumentUploadUrlMutation({
          saleId: variables.saleId,
          upload: {
            fileName: variables.file.name,
            fileMimeType: variables.file.type,
            fileSize: variables.file.size,
          },
        });

      if (generateSupplierBillingAdjusmentDocumentUploadUrl) {
        try {
          const request = new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open(
              "PUT",
              generateSupplierBillingAdjusmentDocumentUploadUrl.url,
            );
            xhr.onload = () => {
              if (xhr.status >= 200 && xhr.status < 300) {
                resolve(xhr.response);
              } else {
                reject(new Error(xhr.statusText));
              }
            };
            xhr.upload.onprogress = (event) => {
              setProgress(Math.round(100 * (event.loaded / event.total)));
            };
            xhr.onerror = () => {
              reject(new Error(xhr.statusText));
            };
            xhr.send(variables.file);
          });
          await request;

          setData({
            uploadedKey: generateSupplierBillingAdjusmentDocumentUploadUrl.key,
          });

          return {
            uploadedUrl: generateSupplierBillingAdjusmentDocumentUploadUrl.url,
          };
        } catch (error) {
          setError(error as Error);
          throw error;
        }
      }
    } finally {
      setLoading(false);
    }
  };

  return [
    billingAdjustmentDocumentUploadMutation,
    { data, error, loading, progress },
  ];
};
